import { Flex, Icon, Text } from '@chakra-ui/react'
import React, { useEffect, useMemo, useState } from 'react'
import iconPause from '~/assets/svgs/icon-transcript-pause.svg?react'
import iconPlay from '~/assets/svgs/icon-transcript-play.svg?react'
import useTranscriptionLogContext from '~/pages/CallAnalytics/Transcription/context/TranscriptionLogContext'
import { colorPalette } from '~/themes/colors/colorPalette'
import { textStyleKeys } from '~/themes/textStyles'
import { formatMilliseconds, tripLeadingZeroFromTime } from '~/utils/formatter'

import { toNounCasing } from '@mangovoice/core'

interface TranscriptEntryHeaderProps {
  transcriptId: string
  personId: number
  personName: string
  beginOffsetMillis: number
  isActive: boolean
  isHovered: boolean
  isPlaying: boolean
}
const TranscriptEntryHeader = ({
  transcriptId,
  personName,
  beginOffsetMillis,
  isActive,
  isHovered,
  isPlaying,
}: TranscriptEntryHeaderProps) => {
  const [wasActive, setWasActive] = useState<boolean>()
  const logContext = useTranscriptionLogContext()

  //keep track of whether or not this component was active to control animation states
  useEffect(() => {
    if (isActive) {
      setWasActive(true)
    }

    return () => {
      setWasActive(undefined)
    }
  }, [isActive])

  //reset wasActive so animations don't trigger on subsequent rerenders from things like hover state changes
  useEffect(() => {
    if (wasActive && !isActive) {
      setWasActive(false)
    }
  }, [wasActive, isActive])

  const handleClick = () => {
    if (isActive) {
      if (isPlaying) {
        logContext?.onPause?.()
      } else {
        logContext?.onPlay?.()
      }
    } else {
      logContext?.onPlay?.(beginOffsetMillis)
    }
  }

  const jsx = useMemo(
    () => (
      <Flex flex="1" height="25px" alignItems="flex-end" gap="0.5rem">
        <Flex
          onClick={handleClick}
          sx={{
            alignItems: 'center',
            cursor: 'pointer',
            minWidth: '56px',
            padding: '2px 6px',
            justifyContent: 'space-between',
            borderRadius: '4px',
            backgroundColor: isActive
              ? colorPalette.blue[300]
              : isHovered
                ? colorPalette.gray[500]
                : colorPalette.gray[100],
          }}
        >
          <Text
            textStyle={textStyleKeys.Body4_Bold}
            textColor={
              isActive
                ? colorPalette.blue[900]
                : isHovered
                  ? colorPalette.gray[1300]
                  : colorPalette.gray[600]
            }
          >
            {tripLeadingZeroFromTime(formatMilliseconds(beginOffsetMillis))}
          </Text>
          <Icon
            as={isActive && isPlaying ? iconPause : iconPlay}
            textColor={
              isActive ? 'blue.900' : isHovered ? 'gray.1300' : 'transparent'
            }
            height="8px"
            width="7px"
          />
        </Flex>
        <Text
          textStyle={textStyleKeys.Body4_Bold}
          color={
            isHovered || isActive
              ? colorPalette.gray[1300]
              : colorPalette.gray[600]
          }
        >
          {toNounCasing(personName)}
        </Text>
      </Flex>
    ),
    [isActive, isHovered, transcriptId, isPlaying],
  )

  return jsx
}

export default TranscriptEntryHeader
